import { useParams } from "crossroad";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { getArticleById, fieldGuideArticles } from "@/data/field-guide/field-guide-data";
import { useViewTransitionNavigate } from "@/lib/hooks/use-view-transition";
import { ArrowLeft, ChevronLeft, ChevronRight } from "lucide-react";
import ReactMarkdown from "react-markdown";
import HowWeUseAIPage from "./how-we-use-ai-page";

export default function FieldGuideArticle() {
    const params = useParams('/app/field-guide/:id');
    const [, navigate] = useViewTransitionNavigate();
    const hideBack = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('hideBack') === 'true';

    const article = params?.id ? getArticleById(params.id) : null;

    if (params?.id === 'how-we-use-ai') {
        return <HowWeUseAIPage />;
    }

    if (!article) {
        return (
            <div className="flex flex-col h-full items-center justify-center p-6">
                <Card className="max-w-md">
                    <CardContent className="pt-6 text-center space-y-4">
                        <p className="text-muted-foreground">Article not found</p>
                        <Button onClick={() => navigate('/app/field-guide')}>
                            <ArrowLeft className="mr-2 h-4 w-4" />
                            Back to Field Guide
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    // Find previous and next articles
    const currentIndex = fieldGuideArticles.findIndex(a => a.id === article.id);
    const previousArticle = currentIndex > 0 ? fieldGuideArticles[currentIndex - 1] : null;
    const nextArticle = currentIndex < fieldGuideArticles.length - 1 ? fieldGuideArticles[currentIndex + 1] : null;

    return (
        <div className="flex flex-col h-full overflow-y-auto">
            <div className="flex-1 p-6 space-y-6">
                {/* Article Header */}
                <div className="space-y-3">
                    <h1 className="text-4xl font-bold tracking-tight">{article.title}</h1>
                    {article.subtitle && (
                        <p className="text-xl text-muted-foreground italic">{article.subtitle}</p>
                    )}
                </div>

                {/* Article Content */}
                <Card>
                    <CardContent className="pt-6">
                        <div className="prose prose-slate dark:prose-invert max-w-none">
                            <ReactMarkdown
                                components={{
                                    // Custom styling for markdown elements
                                    h2: ({ children }) => (
                                        <h2 className="text-2xl font-bold mt-8 mb-4 first:mt-0">{children}</h2>
                                    ),
                                    h3: ({ children }) => (
                                        <h3 className="text-xl font-semibold mt-6 mb-3">{children}</h3>
                                    ),
                                    p: ({ children }) => (
                                        <p className="mb-4 leading-relaxed text-base">{children}</p>
                                    ),
                                    strong: ({ children }) => (
                                        <strong className="font-semibold text-foreground">{children}</strong>
                                    ),
                                    ul: ({ children }) => (
                                        <ul className="list-disc pl-6 mb-4 space-y-2">{children}</ul>
                                    ),
                                    li: ({ children }) => (
                                        <li className="leading-relaxed">{children}</li>
                                    ),
                                    blockquote: ({ children }) => (
                                        <blockquote className="border-l-4 border-primary pl-4 italic my-4 text-muted-foreground">
                                            {children}
                                        </blockquote>
                                    ),
                                }}
                            >
                                {article.content}
                            </ReactMarkdown>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}

